import { AuthService } from './auth.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, tap, fromEvent, merge } from 'rxjs';
import { SessionService } from '@/common/auth/auth-session.service';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Router } from '@angular/router';

@Injectable({
	providedIn: 'root'
})
export class AuthTimeoutService {
	private inactivityTimeout: number = environment.session.inActivityTimeout;
	private warnBefore: number = environment.session.warnBefore;
	private userActivity: Subject<void> = new Subject<void>();
	private activityObservable$?: Observable<Event>;
	private activitySubscription?: Subscription;
	private inactivityTimer?: ReturnType<typeof setTimeout>;
	private dialogTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private readonly authService: AuthService,
		private readonly translate: TranslateService,
		private sessionService: SessionService,
		private confirmationService: ConfirmationService,
		private messageService: MessageService,
		private router: Router,
	) {
		this.monitorAuthStatus();
	}

	private monitorAuthStatus(): void {
		this
			.sessionService
			.isAuthenticated
			.subscribe(
				isAuthenticated => isAuthenticated.valueOf()
					? this.enableMonitoring()
					: this.disableMonitoring()
			);
	}

	private enableMonitoring(): void {
		this.activityObservable$ = merge(
			fromEvent(window, 'mousemove'),
			fromEvent(window, 'click'),
			fromEvent(window, 'keydown'),
			fromEvent(window, 'scroll')
		);

		this.activityObservable$
			.pipe(debounceTime(1000))
			.subscribe((): void => {
				this.userActivity.next();
			});

		this.activitySubscription = this.userActivity
			.pipe(debounceTime(this.inactivityTimeout - this.warnBefore))
			.subscribe((): void => {
				this.resetTimer();
			});

		this.resetTimer();
	}

	private disableMonitoring() {
		clearTimeout(this.inactivityTimer);
		clearTimeout(this.dialogTimeout);
		if (this.activitySubscription) {
			this.activitySubscription.unsubscribe();
		}
	}

	private resetTimer() {
		clearTimeout(this.inactivityTimer);
		clearTimeout(this.dialogTimeout);
		this.inactivityTimer = setTimeout(() => this.showTimeoutWarning(), this.inactivityTimeout - this.warnBefore);
	}

	private showTimeoutWarning() {
		// translate
		this.translate.get([
			'generic.session.timeoutWarningMessage',
			'generic.session.timeoutWarningHeader',
			'generic.session.extendSuccessSummary',
			'generic.session.extendSuccessDetail',
		]).subscribe(translations => {
			// confirm dialog
			this.confirmationService
				.confirm({
					message: translations['generic.session.timeoutWarningMessage'],
					header: translations['generic.session.timeoutWarningHeader'],
					icon: 'pi pi-exclamation-triangle',
					accept: (): void => {
						clearTimeout(this.dialogTimeout);
						this.resetTimer();
						this.messageService.add({
							severity: 'info',
							summary: translations['generic.session.extendSuccessSummary'],
							detail: translations['generic.session.extendSuccessDetail'],
							life: 3000,
						});
					},
					reject: async (): Promise<void> => {
						clearTimeout(this.dialogTimeout);
						this.dismissSession();
					}
				});
		});

		this.dialogTimeout = setTimeout(async (): Promise<void> => {
			this.dismissSession();
		}, this.warnBefore);
	}

	dismissSession(): void {
		this.disableMonitoring();
		this.confirmationService.close();
		this
			.authService
			.logout()
			.pipe(
				tap(() => {
					this
						.translate
						.get([
							'generic.session.expiredWarningSummary',
							'generic.session.expiredWarningDetail'
						])
						.subscribe(translations => {
							this
								.messageService
								.add({
									severity: 'warning',
									summary: translations['generic.session.expiredWarningSummary'],
									detail: translations['generic.session.expiredWarningDetail'],
									life: 5000,
								});
						});
					// navigate to login
					this.router.navigate(['/login']);
				}),
			)
			.subscribe();
	}
}
